import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { TrendUp, TrendDown, ArrowsLeftRight, Lightning, ArrowRight, MagnifyingGlass, CaretUp, CaretDown, Fire, Minus } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { playerToSlug } from "@/lib/playerSlug";

const PC = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24", PICK: "#ccff00" };

function PosBadge({ pos }) {
  if (pos === "PICK") return <span className="text-[9px] px-1.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span>;
  return <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[9px] font-bold uppercase rounded-sm pos-bg-${(pos||"").toLowerCase()} pos-${(pos||"").toLowerCase()}`}>{pos}</span>;
}

function TradeCard({ trade }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-3">
      {trade.featuring && <p className="text-[9px] text-[#ccff00] uppercase tracking-wider mb-2">Involving {trade.featuring}</p>}
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-1">
          <p className="text-[8px] text-[#555] uppercase font-bold">SIDE A</p>
          {(trade.side_a||[]).map((p,i) => (
            <div key={i} className="flex items-center gap-1.5">
              <PosBadge pos={p.position} />
              {p.position !== "PICK" ? <Link to={`/players/${playerToSlug(p.name)}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link> : <span className="text-xs text-white">{p.name}</span>}
            </div>
          ))}
        </div>
        <ArrowsLeftRight size={12} className="text-[#444] mt-2" />
        <div className="flex-1 space-y-1">
          <p className="text-[8px] text-[#555] uppercase font-bold">SIDE B</p>
          {(trade.side_b||[]).map((p,i) => (
            <div key={i} className="flex items-center gap-1.5">
              <PosBadge pos={p.position} />
              {p.position !== "PICK" ? <Link to={`/players/${playerToSlug(p.name)}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link> : <span className="text-xs text-white">{p.name}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WeekNav({ exclude }) {
  const pages = [
    { href: "/weekly/trades", label: "Trade Leaders" },
    { href: "/weekly/waiver-wire", label: "Waiver Wire" },
    { href: "/weekly/movers", label: "Value Movers" },
  ].filter(p => p.href !== exclude);
  return (
    <div className="flex gap-2 mb-6">
      {pages.map(p => <Link key={p.href} to={p.href} className="text-[10px] px-3 py-1.5 border border-[#262626] text-[#a3a3a3] no-underline rounded-sm hover:border-[#444]">{p.label}</Link>)}
      <Link to="/trades" className="text-[10px] px-3 py-1.5 border border-[#ccff00]/30 text-[#ccff00] no-underline rounded-sm hover:bg-[#ccff00]/10">Full Database</Link>
    </div>
  );
}

/* ============ Weekly Trades Page ============ */
export function WeeklyTrades() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/weekly/trades?week=${week}`);
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [week]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="This Week in Dynasty Trades: Most-Traded Players & Trending Deals — Dynasty Trade Maker" description="See which dynasty players are being traded the most this week. Real trade data from 6,976+ Sleeper leagues, updated weekly." path="/weekly/trades" />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">WEEKLY PULSE</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2">THIS WEEK IN <span className="text-[#ccff00]">DYNASTY TRADES</span></h1>
        <p className="text-sm text-[#a3a3a3] mb-4">The most-traded players across {(data?.total_all_trades||0).toLocaleString()} real dynasty trades. See who's moving and what they're going for.</p>

        <WeekNav exclude="/weekly/trades" />

        {/* Week selector */}
        {data?.available_weeks?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-6">
            <button onClick={() => setWeek(0)} className={`px-2.5 py-1 text-[10px] font-bold rounded-sm ${week === 0 ? 'bg-[#ccff00] text-black' : 'border border-[#262626] text-[#888] hover:border-[#444]'}`}>All</button>
            {data.available_weeks.filter(w => w.week > 0).slice(0, 18).map(w => (
              <button key={w.week} onClick={() => setWeek(w.week)} className={`px-2.5 py-1 text-[10px] font-bold rounded-sm ${week === w.week ? 'bg-[#ccff00] text-black' : 'border border-[#262626] text-[#888] hover:border-[#444]'}`}>Wk {w.week}</button>
            ))}
          </div>
        )}

        {loading ? <div className="h-64 skeleton rounded-sm" /> : (
          <>
            <div className="border border-[#262626] overflow-hidden mb-6">
              <div className="grid grid-cols-5 bg-[#0a0a0a] px-3 py-2 border-b border-[#262626] text-[9px] font-bold uppercase text-[#555]">
                <span>#</span><span className="col-span-2">Player</span><span>Pos</span><span className="text-right">Trades</span>
              </div>
              {(data?.trade_leaders || []).map((p, i) => (
                <Link key={i} to={`/trades/${p.slug}`} className="grid grid-cols-5 px-3 py-2 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors">
                  <span className="text-xs text-[#555]">{i+1}</span>
                  <span className="text-xs text-white col-span-2">{p.name}</span>
                  <span className="text-xs" style={{color: PC[p.position]}}>{p.position}</span>
                  <span className="text-xs font-mono text-[#ccff00] text-right">{p.trade_count}</span>
                </Link>
              ))}
            </div>

            {data?.sample_trades?.length > 0 && (
              <div className="mb-6">
                <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">SAMPLE TRADES</h2>
                <div className="space-y-2">
                  {data.sample_trades.map((t, i) => <TradeCard key={i} trade={t} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ============ Waiver Wire Report ============ */
export function WaiverWireReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/weekly/waiver-wire`);
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Waiver Wire Report: Most-Added & Dropped Players This Week — Dynasty Trade Maker" description="See which players are being added and dropped the most across Sleeper dynasty leagues this week. Real-time trending data cross-referenced with dynasty values." path="/weekly/waiver-wire" />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">WEEKLY PULSE</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2">WAIVER WIRE <span className="text-[#ccff00]">REPORT</span></h1>
        <p className="text-sm text-[#a3a3a3] mb-4">Most-added and most-dropped players across all Sleeper leagues this week, cross-referenced with dynasty values.</p>

        <WeekNav exclude="/weekly/waiver-wire" />

        {loading ? <div className="h-64 skeleton rounded-sm" /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Adds */}
            <div>
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#4ade80] mb-3 flex items-center gap-1.5"><TrendUp size={12} /> MOST ADDED</h2>
              <div className="border border-[#262626] overflow-hidden">
                {(data?.adds || []).slice(0, 15).map((p, i) => (
                  <Link key={i} to={`/players/${p.slug}`} className="flex items-center justify-between px-3 py-2 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] text-[#555] w-4">{i+1}</span>
                      <PosBadge pos={p.position} />
                      <span className="text-xs text-white truncate">{p.name}</span>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="text-xs font-mono text-[#4ade80]">{p.add_count?.toLocaleString()}</p>
                      {p.value > 0 && <p className="text-[9px] text-[#555]">Val: {p.value.toLocaleString()}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Drops */}
            <div>
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#ff3b30] mb-3 flex items-center gap-1.5"><TrendDown size={12} /> MOST DROPPED</h2>
              <div className="border border-[#262626] overflow-hidden">
                {(data?.drops || []).slice(0, 15).map((p, i) => (
                  <Link key={i} to={`/players/${p.slug}`} className="flex items-center justify-between px-3 py-2 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] text-[#555] w-4">{i+1}</span>
                      <PosBadge pos={p.position} />
                      <span className="text-xs text-white truncate">{p.name}</span>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="text-xs font-mono text-[#ff3b30]">{p.drop_count?.toLocaleString()}</p>
                      {p.value > 0 && <p className="text-[9px] text-[#555]">Val: {p.value.toLocaleString()}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Value Movers ============ */
export function ValueMovers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/weekly/movers`);
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Value Movers: Biggest Risers & Fallers This Month — Dynasty Trade Maker" description="The biggest dynasty value risers and fallers over the last 30 days. Live FantasyCalc data showing which players are gaining or losing trade value." path="/weekly/movers" />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">WEEKLY PULSE</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2">VALUE <span className="text-[#ccff00]">MOVERS</span></h1>
        <p className="text-sm text-[#a3a3a3] mb-4">The biggest dynasty value risers and fallers over the last 30 days. Catch the market shifts before your league-mates.</p>

        <WeekNav exclude="/weekly/movers" />

        {loading ? <div className="h-64 skeleton rounded-sm" /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Risers */}
            <div>
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#4ade80] mb-3 flex items-center gap-1.5"><CaretUp size={14} weight="fill" /> RISERS</h2>
              <div className="border border-[#262626] overflow-hidden">
                {(data?.risers || []).map((p, i) => (
                  <Link key={i} to={`/players/${p.slug}`} className="flex items-center justify-between px-3 py-2.5 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <PosBadge pos={p.position} />
                      <div className="min-w-0">
                        <p className="text-xs text-white truncate">{p.name}</p>
                        <p className="text-[9px] text-[#555]">{p.team} · #{p.rank}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="text-xs font-mono text-[#4ade80]">+{p.trend}</p>
                      <p className="text-[9px] text-[#555]">{p.value.toLocaleString()}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Fallers */}
            <div>
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#ff3b30] mb-3 flex items-center gap-1.5"><CaretDown size={14} weight="fill" /> FALLERS</h2>
              <div className="border border-[#262626] overflow-hidden">
                {(data?.fallers || []).map((p, i) => (
                  <Link key={i} to={`/players/${p.slug}`} className="flex items-center justify-between px-3 py-2.5 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <PosBadge pos={p.position} />
                      <div className="min-w-0">
                        <p className="text-xs text-white truncate">{p.name}</p>
                        <p className="text-[9px] text-[#555]">{p.team} · #{p.rank}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="text-xs font-mono text-[#ff3b30]">{p.trend}</p>
                      <p className="text-[9px] text-[#555]">{p.value.toLocaleString()}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
